import React, { useState } from "react"; // useState kept for form fields + localError
import { useNavigate } from "react-router-dom";
import { useSendEmail } from "../../hooks/useSendEmail";

const PRIMARY = "#000c66";
const GOLD = "#ffc000";

const STYLES = `
  .cf-field { position: relative; margin-bottom: 0; }
  .cf-label {
    display: block;
    font-family: Abel, sans-serif;
    font-size: 10px;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: rgba(0,12,102,0.5);
    margin-bottom: 7px;
    transition: color 0.2s;
  }
  .cf-field:focus-within .cf-label { color: ${GOLD}; }
  .cf-input, .cf-textarea {
    width: 100%;
    background: transparent;
    border: none;
    border-bottom: 1.5px solid rgba(0,12,102,0.18);
    border-radius: 0;
    padding: 8px 0 10px;
    font-family: Abel, sans-serif;
    font-size: 15px;
    color: ${PRIMARY};
    outline: none;
    box-sizing: border-box;
    transition: border-color 0.25s;
    resize: none;
  }
  .cf-input::placeholder, .cf-textarea::placeholder { color: rgba(0,12,102,0.22); }
  .cf-input:focus, .cf-textarea:focus { border-bottom-color: ${GOLD}; }
  .cf-btn {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    background: ${PRIMARY};
    color: #fff;
    font-family: Abel, sans-serif;
    font-size: 11px;
    letter-spacing: 0.24em;
    text-transform: uppercase;
    border: 1.5px solid ${PRIMARY};
    padding: 14px 36px;
    cursor: pointer;
    transition: background 0.22s, color 0.22s;
  }
  .cf-btn:hover:not(:disabled) { background: transparent; color: ${PRIMARY}; }
  .cf-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .cf-btn svg { width: 16px; height: 16px; }
  .cf-error {
    font-family: Abel, sans-serif;
    font-size: 12px;
    color: #c0392b;
    letter-spacing: 0.05em;
    display: flex;
    align-items: center;
    gap: 7px;
  }
  .cf-success {
    font-family: Abel, sans-serif;
    font-size: 13px;
    color: #1a6b3c;
    letter-spacing: 0.05em;
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const validateEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.toLowerCase());

export const ContactForm = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [localError, setLocalError] = useState("");
  const { send, sending, sent, error: sendError } = useSendEmail();

  const set = (field) => (e) => {
    setForm((p) => ({ ...p, [field]: e.target.value }));
    setLocalError("");
  };

  const error = localError || sendError || "";
  const success = sent;

  const submit = async (e) => {
    e.preventDefault();
    const { name, email, phone, message } = form;
    if (!name.trim()) return setLocalError("Please enter your name.");
    if (!validateEmail(email))
      return setLocalError("Please enter a valid email address.");
    if (!phone.trim()) return setLocalError("Please enter your phone number.");
    if (!message.trim()) return setLocalError("Please write a message.");

    const ok = await send({
      from_name: name,
      from_email: email,
      phone,
      message,
    });
    if (ok) {
      setForm({ name: "", email: "", phone: "", message: "" });
      setTimeout(() => navigate("/"), 4000);
    }
  };

  return (
    <>
      <style>{STYLES}</style>

      <section style={{ background: "#fff", padding: "64px 16px 80px" }}>
        <div style={{ maxWidth: 680, margin: "0 auto" }}>
          {/* Section label */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginBottom: 40,
            }}
          >
            <div style={{ width: 32, height: 2, background: GOLD }} />
            <span
              style={{
                fontFamily: "Abel, sans-serif",
                fontSize: 10,
                letterSpacing: "0.28em",
                textTransform: "uppercase",
                color: PRIMARY,
              }}
            >
              Send a Message
            </span>
          </div>

          {success ? (
            <div
              style={{
                padding: "48px 0",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: "50%",
                  background: `${GOLD}22`,
                  border: `2px solid ${GOLD}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 20px",
                }}
              >
                <svg viewBox="0 0 20 20" fill="none" width="22" height="22">
                  <path
                    d="M4 10l4 4 8-8"
                    stroke={PRIMARY}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <p
                style={{
                  fontFamily: "Aref Ruqaa, serif",
                  fontSize: "1.5rem",
                  color: PRIMARY,
                  margin: "0 0 8px",
                }}
              >
                Message Received
              </p>
              <p
                style={{
                  fontFamily: "Abel, sans-serif",
                  fontSize: 14,
                  color: "rgba(0,12,102,0.5)",
                  margin: 0,
                }}
              >
                We'll get back to you shortly. Redirecting home…
              </p>
            </div>
          ) : (
            <form onSubmit={submit} noValidate>
              {/* Name + Email row */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "0 40px",
                  marginBottom: 36,
                }}
                className="cf-grid-2"
              >
                <div className="cf-field">
                  <label className="cf-label" htmlFor="cf-name">
                    Full Name
                  </label>
                  <input
                    id="cf-name"
                    className="cf-input"
                    type="text"
                    placeholder="e.g. John Adewale"
                    value={form.name}
                    onChange={set("name")}
                    autoComplete="name"
                  />
                </div>
                <div className="cf-field">
                  <label className="cf-label" htmlFor="cf-email">
                    Email Address
                  </label>
                  <input
                    id="cf-email"
                    className="cf-input"
                    type="email"
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={set("email")}
                    autoComplete="email"
                  />
                </div>
              </div>

              {/* Phone */}
              <div className="cf-field" style={{ marginBottom: 36 }}>
                <label className="cf-label" htmlFor="cf-phone">
                  Phone Number
                </label>
                <input
                  id="cf-phone"
                  className="cf-input"
                  type="tel"
                  placeholder="+234 800 000 0000"
                  value={form.phone}
                  onChange={set("phone")}
                  autoComplete="tel"
                />
              </div>

              {/* Message */}
              <div className="cf-field" style={{ marginBottom: 36 }}>
                <label className="cf-label" htmlFor="cf-message">
                  Message
                </label>
                <textarea
                  id="cf-message"
                  className="cf-textarea"
                  rows={5}
                  placeholder="How can we help you?"
                  value={form.message}
                  onChange={set("message")}
                />
              </div>

              {/* Error */}
              {error && (
                <div className="cf-error" style={{ marginBottom: 20 }}>
                  <svg viewBox="0 0 20 20" fill="none" width="14" height="14">
                    <circle
                      cx="10"
                      cy="10"
                      r="9"
                      stroke="#c0392b"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M10 6v5m0 3v.01"
                      stroke="#c0392b"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                  {error}
                </div>
              )}

              {/* Submit */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 24,
                  flexWrap: "wrap",
                }}
              >
                <button type="submit" className="cf-btn" disabled={sending}>
                  {sending ? (
                    <>
                      <svg viewBox="0 0 24 24" fill="none">
                        <circle
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeOpacity="0.25"
                        />
                        <path
                          d="M12 2a10 10 0 0 1 10 10"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        >
                          <animateTransform
                            attributeName="transform"
                            type="rotate"
                            from="0 12 12"
                            to="360 12 12"
                            dur="0.8s"
                            repeatCount="indefinite"
                          />
                        </path>
                      </svg>
                      Sending…
                    </>
                  ) : (
                    <>
                      Send Message
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="22" y1="2" x2="11" y2="13" />
                        <polygon points="22 2 15 22 11 13 2 9 22 2" />
                      </svg>
                    </>
                  )}
                </button>

                <span
                  style={{
                    fontFamily: "Abel, sans-serif",
                    fontSize: 11,
                    color: "rgba(0,12,102,0.35)",
                    letterSpacing: "0.06em",
                  }}
                >
                  cccsalvationparishlagos@gmail.com
                </span>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* Responsive: stack 2-col grid on mobile */}
      <style>{`
        @media (max-width: 600px) {
          .cf-grid-2 { grid-template-columns: 1fr !important; gap: 0 !important; }
          .cf-grid-2 > .cf-field { margin-bottom: 36px; }
        }
      `}</style>
    </>
  );
};
